/**
 * Canonical Project Participants model.
 *
 * Project-wide person assignments are exactly:
 *   Project Manager · Contributor · Observer
 *
 * Company participation (Investee · Investor · Partner · Vendor) is a
 * company↔project relationship, never a person-level project role.
 *
 * Validator / Reporting Recipient / Decision Participant remain object-scoped
 * and must not appear in the Project Participants role selector.
 */

import type { RelationshipType } from './company';
import type { ScopedAccessLevel, ScopedRelationshipType } from './authority';

/** Canonical project-wide participant assignment labels (UI / membership). */
export type ProjectAssignmentRole =
  | 'Project Manager'
  | 'Contributor'
  | 'Observer';

/** Canonical relationship tokens for project-wide assignments. */
export type ProjectAssignmentRelationship =
  | 'project_manager'
  | 'project_contributor'
  | 'observer';

/** Company↔project participation types (exact product set). */
export type CompanyProjectParticipation =
  | 'Investee'
  | 'Investor'
  | 'Partner'
  | 'Vendor';

export const PROJECT_ASSIGNMENT_ROLES: readonly ProjectAssignmentRole[] = [
  'Project Manager',
  'Contributor',
  'Observer',
] as const;

export const PROJECT_ASSIGNMENT_LABELS: Readonly<Record<ProjectAssignmentRole, string>> = {
  'Project Manager': 'Project Manager',
  Contributor: 'Contributor',
  Observer: 'Observer',
};

export const COMPANY_PROJECT_PARTICIPATION_TYPES: readonly CompanyProjectParticipation[] = [
  'Investee',
  'Investor',
  'Partner',
  'Vendor',
] as const;

const ASSIGNMENT_TO_SCOPED: Readonly<Record<ProjectAssignmentRole, ProjectAssignmentRelationship>> = {
  'Project Manager': 'project_manager',
  Contributor: 'project_contributor',
  Observer: 'observer',
};

const SCOPED_TO_ASSIGNMENT: Readonly<Record<ProjectAssignmentRelationship, ProjectAssignmentRole>> = {
  project_manager: 'Project Manager',
  project_contributor: 'Contributor',
  observer: 'Observer',
};

const ASSIGNMENT_ACCESS_LEVEL: Readonly<Record<ProjectAssignmentRole, ScopedAccessLevel>> = {
  'Project Manager': 'manage',
  Contributor: 'contribute',
  Observer: 'view',
};

/** Roles that must never appear in the Project Participants selector. */
export const FORBIDDEN_PROJECT_PARTICIPANT_ROLES = [
  'Validator',
  'Reporting Recipient',
  'Decision Participant',
  'Investee',
  'Investor',
  'Partner',
  'Vendor',
  'Execution Lead',
  'Company',
] as const;

export function isProjectAssignmentRole(value: string | null | undefined): value is ProjectAssignmentRole {
  return Boolean(value && PROJECT_ASSIGNMENT_ROLES.includes(value as ProjectAssignmentRole));
}

export function projectAssignmentToScopedType(
  role: ProjectAssignmentRole,
): ProjectAssignmentRelationship {
  return ASSIGNMENT_TO_SCOPED[role];
}

export function scopedTypeToProjectAssignment(
  type: ScopedRelationshipType | string,
): ProjectAssignmentRole | null {
  if (type in SCOPED_TO_ASSIGNMENT) {
    return SCOPED_TO_ASSIGNMENT[type as ProjectAssignmentRelationship];
  }
  return null;
}

export function projectAssignmentAccessLevel(role: ProjectAssignmentRole): ScopedAccessLevel {
  return ASSIGNMENT_ACCESS_LEVEL[role];
}

/**
 * Derive company participation from the company↔project relationship.
 * Does not invent a type when the relationship is internal/unknown.
 */
export function deriveCompanyParticipation(
  relationshipType: RelationshipType | string | null | undefined,
): CompanyProjectParticipation | null {
  switch (relationshipType) {
    case 'investee':
    case 'capital_recipient':
    case 'portfolio_company':
    case 'borrower':
    case 'sponsor':
    case 'project_company':
      return 'Investee';
    case 'investor':
      return 'Investor';
    case 'partner':
      return 'Partner';
    case 'vendor':
      return 'Vendor';
    default:
      return null;
  }
}

/**
 * Map legacy Title-Case project_memberships roles into canonical assignments.
 * Company-side roles (Investor/Vendor/Partner/Company) are not kept as person
 * project roles — they migrate to Contributor/Observer access seats only.
 */
export function migrateLegacyMembershipRole(
  legacyRole: string | null | undefined,
): ProjectAssignmentRole | null {
  switch (legacyRole) {
    case 'Project Manager':
    case 'project_manager':
      return 'Project Manager';
    case 'Contributor':
    case 'project_contributor':
    case 'Vendor':
    case 'vendor':
      return 'Contributor';
    case 'Observer':
    case 'observer':
    case 'Investor':
    case 'investor':
    case 'Partner':
    case 'partner':
    case 'Company':
    case 'Client':
    case 'client':
      return 'Observer';
    default:
      return null;
  }
}

/** Contextual primary-designation label for the Add Participant drawer. */
export function primaryDesignationLabel(role: ProjectAssignmentRole): string {
  return `Mark as primary ${role}`;
}
